import React from "react"
import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer"
import { cn } from "../utils"
import { useDrawerContext } from "./drawer-context"
import type { DrawerSide } from "./drawer-context"

export type DrawerContentProps = {
  children?: React.ReactNode
  className?: string
}

// Parity with `[data-reach-dialog-overlay]`, so a drawer and a legacy modal
// stack in DOM order instead of one always winning. Menu (5001) and Tooltip
// (5002) sit above it, so popups opened from inside a drawer still show.
const OVERLAY_Z_INDEX = "z-[51]"

const backdropStyles = [
  "fixed inset-0 bg-black/50",
  OVERLAY_Z_INDEX,
  "transition-opacity duration-300 ease-out",
  "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0"
]

const viewportStyles = [
  "fixed inset-0 flex pointer-events-none",
  "[--drawer-inset:0.5rem] p-(--drawer-inset)",
  OVERLAY_Z_INDEX
]

const popupStyles = [
  "pointer-events-auto flex flex-col min-h-0 bg-white shadow",
  "transition-transform duration-300 ease-in-out"
]

const sidePanelStyles = "rounded h-full w-4/5 max-w-2xl"

const bottomSheetStyles = "rounded w-full max-h-[90dvh]"

// Which edge the panel sits behind before it enters and after it leaves. Below
// the md breakpoint the panel is a sheet, so it always comes up from "bottom".
type DrawerEdge = DrawerSide | "bottom"

const ALIGNMENT: Record<DrawerEdge, string> = {
  right: "justify-end",
  left: "justify-start",
  bottom: "items-end"
}

const OFF_SCREEN: Record<DrawerEdge, string> = {
  right:
    "data-[starting-style]:translate-x-[calc(100%_+_var(--drawer-inset))] data-[ending-style]:translate-x-[calc(100%_+_var(--drawer-inset))]",
  left: "data-[starting-style]:translate-x-[calc(-100%_-_var(--drawer-inset))] data-[ending-style]:translate-x-[calc(-100%_-_var(--drawer-inset))]",
  bottom:
    "data-[starting-style]:translate-y-[calc(100%_+_var(--drawer-inset))] data-[ending-style]:translate-y-[calc(100%_+_var(--drawer-inset))]"
}

/**
 * The drawer panel with included portal, backdrop, viewport and popup.
 *
 * Must contain a `Drawer.Title` to provide an accessible name for the panel.
 */
export function DrawerContent({ children, className }: DrawerContentProps) {
  const { side, isSidePanel } = useDrawerContext()
  const edge: DrawerEdge = isSidePanel ? side : "bottom"

  return (
    <DrawerPrimitive.Portal>
      <DrawerPrimitive.Backdrop
        data-slot="drawer-backdrop"
        className={cn(backdropStyles)}
      />
      <DrawerPrimitive.Viewport
        data-slot="drawer-viewport"
        className={cn(viewportStyles, ALIGNMENT[edge])}
      >
        <DrawerPrimitive.Popup
          data-slot="drawer-content"
          className={cn(
            popupStyles,
            isSidePanel ? sidePanelStyles : bottomSheetStyles,
            OFF_SCREEN[edge],
            className
          )}
        >
          {!isSidePanel && (
            <div className="flex shrink-0 justify-center pt-3 pb-1">
              <div
                data-slot="drawer-drag-handle"
                className="h-1 w-10 rounded-full bg-gray-300"
                aria-hidden="true"
              />
            </div>
          )}

          <DrawerPrimitive.Content className="flex flex-col flex-1 min-h-0">
            {children}
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPrimitive.Portal>
  )
}

DrawerContent.displayName = "Drawer.Content"
